import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetch2faStatus } from '../api';
import { Spinner } from './Spinner';

/**
 * Enforces 2FA enrollment when the deployment requires it. Runs inside the
 * authenticated area (after ProtectedRoute). If 2FA is required but not yet
 * enabled, every route redirects to /security/enroll — a focused, guided
 * enrollment page (rendered outside this gate in App.tsx) — until the user
 * enrolls.
 */
export function RequireTwoFactor({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: fetch2faStatus,
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div className="grid min-h-screen place-items-center text-ink-muted">
        <Spinner />
      </div>
    );
  }

  // On error, don't hard-block the app — fail open to the normal UI.
  const mustEnroll = !isError && data?.required && !data.enabled;
  if (mustEnroll && location.pathname !== '/security/enroll') {
    return <Navigate to="/security/enroll" replace />;
  }

  return <>{children}</>;
}
